import { useRef, useCallback, useState } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import type { Experiment } from "../../types/experiment";
import { Button } from "../ui/button";
import { Copy, Download, Upload, Pencil, Check, X } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

interface JsonPreviewProps {
  experiment: Experiment;
  onChange: (experiment: Experiment) => void;
  onImport: () => void;
}

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button type="button" variant="ghost" size="icon" onClick={onClick} className="h-7 w-7">
            <Icon className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function JsonPreview({ experiment, onChange, onImport }: JsonPreviewProps) {
  const editorRef = useRef<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  const handleEditorMount: OnMount = (editor) => {
    editorRef.current = editor;
  };

  const handleCopy = useCallback(() => {
    const json = JSON.stringify(experiment, null, 2);
    navigator.clipboard.writeText(json);
  }, [experiment]);

  const handleDownload = useCallback(() => {
    const json = JSON.stringify(experiment, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${experiment.id || "experiment"}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [experiment]);

  const handleApplyEdit = useCallback(() => {
    try {
      const parsed = JSON.parse(editValue);
      onChange(parsed);
      setIsEditing(false);
    } catch {
      // Invalid JSON
    }
  }, [editValue, onChange]);

  const handleStartEdit = useCallback(() => {
    setEditValue(JSON.stringify(experiment, null, 2));
    setIsEditing(true);
  }, [experiment]);

  const jsonString = JSON.stringify(experiment, null, 2);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-surface1 bg-mantle/80">
        <span className="text-[11px] font-medium text-overlay1 uppercase tracking-wider">JSON</span>
        <div className="flex items-center gap-0.5">
          <ToolbarButton icon={Copy} label="Copy" onClick={handleCopy} />
          <ToolbarButton icon={Download} label="Download" onClick={handleDownload} />
          <ToolbarButton icon={Upload} label="Import" onClick={onImport} />
          <ToolbarButton icon={Pencil} label="Edit" onClick={handleStartEdit} />
        </div>
      </div>
      <div className="flex-1">
        {isEditing ? (
          <div className="h-full flex flex-col">
            <Editor
              height="calc(100% - 40px)"
              defaultLanguage="json"
              value={editValue}
              onChange={(val) => setEditValue(val ?? "")}
              onMount={handleEditorMount}
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 12,
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                wordWrap: "on",
                tabSize: 2,
                padding: { top: 8 },
              }}
            />
            <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-surface1">
              <Button type="button" variant="ghost" size="sm" onClick={() => setIsEditing(false)}>
                <X className="h-3.5 w-3.5 mr-1" />
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handleApplyEdit}>
                <Check className="h-3.5 w-3.5 mr-1" />
                Apply
              </Button>
            </div>
          </div>
        ) : (
          <Editor
            height="100%"
            defaultLanguage="json"
            value={jsonString}
            onMount={handleEditorMount}
            theme="vs-dark"
            options={{
              readOnly: true,
              minimap: { enabled: false },
              fontSize: 12,
              lineNumbers: "on",
              scrollBeyondLastLine: false,
              wordWrap: "on",
              tabSize: 2,
              padding: { top: 8 },
              renderLineHighlight: "none",
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              scrollbar: { vertical: "auto", horizontal: "auto" },
            }}
          />
        )}
      </div>
    </div>
  );
}
